import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail } from "lucide-react";
import { BrandLogo } from "../components/BrandLogo";
import { Button } from "../components/ui/button";

const sections = [
    {
        n: 1,
        title: "Introduction",
        body: (
            <>
                At <strong>Vive a lo Grande</strong> (Vivealogrande.com) we value your privacy. This Privacy Policy explains what information we collect, how we use it, with whom we share it, and your rights over it.
            </>
        ),
    },
    {
        n: 2,
        title: "Information We Collect",
        body: (
            <>
                We collect: (a) Google account data at sign-in (name, email, profile picture); (b) financial data you voluntarily enter (transactions, categories, budgets, goals, assets, liabilities, recurring items); (c) messages you send to the money coach; (d) automatic technical data such as IP address, browser type, OS, and usage timestamps.
            </>
        ),
    },
    {
        n: 3,
        title: "How We Use Your Information",
        body: (
            <>
                We use your information to: (a) provide and maintain the Service; (b) authenticate your identity and protect your account; (c) generate charts, analytics, and AI responses from your own data; (d) improve the Service; (e) communicate important updates; (f) meet legal obligations.
            </>
        ),
    },
    {
        n: 4,
        title: "Sharing with Third Parties",
        body: (
            <>
                We do not sell your personal information. We share data only with providers required to operate the app: Google (authentication), AI model providers (which process your assistant questions), hosting and database services. Third parties are bound by confidentiality and may only use your data to serve us.
            </>
        ),
    },
    {
        n: 5,
        title: "Cookies & Similar Technologies",
        body: (
            <>
                We use essential cookies and local browser storage to keep your session active and remember preferences like language. We do not use third-party advertising or tracking cookies for marketing.
            </>
        ),
    },
    {
        n: 6,
        title: "Data Security",
        body: (
            <>
                We use reasonable technical and organizational measures: HTTPS in transit, secure Google OAuth, access controls, per-user data isolation. No electronic transmission or storage is 100% secure, but we do our best to protect you.
            </>
        ),
    },
    {
        n: 7,
        title: "Data Retention",
        body: (
            <>
                We retain your information while your account is active. On deletion request, we remove your data within a reasonable timeframe, except where we must retain some information for legal, accounting, or security purposes.
            </>
        ),
    },
    {
        n: 8,
        title: "Your Rights",
        body: (
            <>
                Depending on jurisdiction, you may have the right to: (a) access personal data we hold; (b) correct inaccuracies; (c) request deletion of your account and data; (d) obtain a portable copy; (e) object to certain uses. Contact <a className="underline decoration-[hsl(var(--accent))]" href="mailto:vivealograndeus@gmail.com">vivealograndeus@gmail.com</a> to exercise these rights.
            </>
        ),
    },
    {
        n: 9,
        title: "Children's Privacy",
        body: (
            <>
                The Service is not directed to children under 13 and we do not knowingly collect their information. If you believe a child provided us data, contact us and we will delete it promptly.
            </>
        ),
    },
    {
        n: 10,
        title: "International Transfers",
        body: (
            <>
                We are located in the State of Tennessee, United States of America. If you access the Service from outside the U.S., your data will be transferred, stored, and processed in the U.S., where data protection laws may differ from your country.
            </>
        ),
    },
    {
        n: 11,
        title: "Changes to this Policy",
        body: (
            <>
                We may update this Privacy Policy occasionally. The updated version will be posted with a new effective date. Continued use signifies acceptance.
            </>
        ),
    },
    {
        n: 12,
        title: "Contact",
        body: (
            <>
                Questions or requests, or to exercise your rights, contact <a className="underline decoration-[hsl(var(--accent))]" href="mailto:vivealograndeus@gmail.com">vivealograndeus@gmail.com</a>. We will respond as quickly as possible.
            </>
        ),
    },
    {
        n: 13,
        title: "Marketing Communications",
        body: (
            <>
                In addition to transactional emails required to operate the Service (authentication, security alerts, account notifications), we may send you marketing emails with financial tips, product updates and the weekly recap — but only if you explicitly opt in at first sign-in. You can revoke consent any time via the "Unsubscribe" link in every marketing email or by writing to <a className="underline decoration-[hsl(var(--accent))]" href="mailto:vivealograndeus@gmail.com">vivealograndeus@gmail.com</a>. We keep a record of your consent (date, time and choice) to comply with <strong>CAN-SPAM</strong> (U.S.), <strong>CASL</strong> (Canada) and <strong>GDPR</strong> (EU) as applicable.
            </>
        ),
    },
];

const Privacy = () => {
    const navigate = useNavigate();
    return (
        <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="privacy-page">
            <header className="sticky top-0 z-30 bg-[hsl(var(--background))]/85 backdrop-blur border-b border-[hsl(var(--border))]">
                <div className="max-w-4xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                    <button
                        data-testid="privacy-back-button"
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </button>
                    <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
                        <BrandLogo size={26} />
                        <span className="font-display font-bold text-base tracking-tight text-[hsl(var(--foreground))]">Vive a lo Grande</span>
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-16">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="flex items-center gap-3 mb-4">
                        <span className="text-[10px] md:text-xs font-bold tracking-[0.28em] text-[hsl(var(--accent))] uppercase">
                            Legal
                        </span>
                        <span className="h-px w-14 bg-[hsl(var(--muted-foreground))]/40" />
                    </div>
                    <h1 className="font-display font-extrabold text-[hsl(var(--foreground))] leading-[1.05] tracking-[-0.02em] text-4xl md:text-6xl">
                        Privacy Policy
                    </h1>
                    <p className="mt-4 text-[hsl(var(--muted-foreground))]">
                        <span className="font-semibold text-[hsl(var(--foreground))]">Last updated:</span> February 1, 2026
                    </p>
                </motion.div>

                <div className="mt-10 md:mt-14 space-y-8">
                    {sections.map((s) => (
                        <section key={s.n} data-testid={`privacy-section-${s.n}`} className="scroll-mt-24">
                            <div className="flex items-baseline gap-4">
                                <span className="font-display font-bold text-[hsl(var(--accent))] text-lg md:text-xl w-8 shrink-0">
                                    {String(s.n).padStart(2, "0")}
                                </span>
                                <h2 className="font-display font-bold text-[hsl(var(--foreground))] text-xl md:text-2xl tracking-tight">
                                    {s.title}
                                </h2>
                            </div>
                            <div className="mt-3 pl-12 text-[hsl(var(--foreground))]/85 leading-relaxed">
                                {s.body}
                            </div>
                        </section>
                    ))}
                </div>

                <div className="mt-16 pt-8 border-t border-[hsl(var(--border))] flex items-center justify-between gap-4 flex-wrap">
                    <a
                        href="mailto:vivealograndeus@gmail.com"
                        className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"
                        data-testid="privacy-contact-link"
                    >
                        <Mail className="w-4 h-4" /> vivealograndeus@gmail.com
                    </a>
                    <div className="flex gap-3">
                        <Button
                            data-testid="privacy-terms-link"
                            onClick={() => navigate("/terms")}
                            variant="outline"
                            className="pill"
                        >
                            Terms of Service
                        </Button>
                        <Button
                            data-testid="privacy-return-home"
                            onClick={() => navigate("/")}
                            className="pill bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white"
                        >
                            Return home
                        </Button>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Privacy;
